import { useCallback, useMemo } from 'react';
import { Input, Typography } from 'antd';
import { neutral } from '@/theme';

const { Text } = Typography;

interface PlatformVersions {
  client: { android: string; ios: string };
  pro: { android: string; ios: string };
}

interface PlatformVersionsEditorProps {
  value: string;
  onChange: (json: string) => void;
  disabled?: boolean;
}

function parseVersions(val: string): PlatformVersions {
  try {
    const parsed = JSON.parse(val);
    if (parsed && typeof parsed === 'object') {
      return {
        client: {
          android: parsed.client?.android ?? '',
          ios: parsed.client?.ios ?? '',
        },
        pro: {
          android: parsed.pro?.android ?? '',
          ios: parsed.pro?.ios ?? '',
        },
      };
    }
  } catch {
    // ignore
  }
  return {
    client: { android: '', ios: '' },
    pro: { android: '', ios: '' },
  };
}

export function PlatformVersionsEditor({
  value,
  onChange,
  disabled = false,
}: PlatformVersionsEditorProps) {
  const versions = useMemo(() => parseVersions(value), [value]);

  const emitChange = useCallback(
    (next: PlatformVersions) => {
      onChange(JSON.stringify(next));
    },
    [onChange],
  );

  const handleChange = useCallback(
    (
      section: 'client' | 'pro',
      platform: 'android' | 'ios',
      val: string,
    ) => {
      const next: PlatformVersions = {
        ...versions,
        [section]: {
          ...versions[section],
          [platform]: val,
        },
      };
      emitChange(next);
    },
    [versions, emitChange],
  );

  const renderSection = (label: string, section: 'client' | 'pro') => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '10px 12px',
        border: `1px solid ${neutral[100]}`,
        borderRadius: 8,
        flex: 1,
        minWidth: 180,
      }}
    >
      <Text strong style={{ fontSize: 13 }}>
        {label}
      </Text>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Text style={{ fontSize: 12, minWidth: 52 }}>Android</Text>
        <Input
          value={versions[section].android}
          onChange={(e) => handleChange(section, 'android', e.target.value)}
          placeholder="2.0.30"
          disabled={disabled}
          size="small"
          style={{ flex: 1 }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Text style={{ fontSize: 12, minWidth: 52 }}>iOS</Text>
        <Input
          value={versions[section].ios}
          onChange={(e) => handleChange(section, 'ios', e.target.value)}
          placeholder="2.0.30"
          disabled={disabled}
          size="small"
          style={{ flex: 1 }}
        />
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 12, flex: 1, flexWrap: 'wrap' }}>
      {renderSection('Client', 'client')}
      {renderSection('Pro', 'pro')}
    </div>
  );
}
